import { useState, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import agencyImage from "@/assets/agency-cta.jpg";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Hero from "@/components/Hero";
import TripCard from "@/components/TripCard";
import TripCardSkeleton from "@/components/TripCardSkeleton";
import BlogCardSkeleton from "@/components/BlogCardSkeleton";
import CreateTripDialog from "@/components/CreateTripDialog";
import Footer from "@/components/Footer";
import AuthDialog from "@/components/AuthDialog";
import SEO from "@/components/SEO";
import OptimizedImage from "@/components/ui/optimized-image";
import PopularCountries from "@/components/PopularCountries";
import ScrollRevealSection from "@/components/ScrollRevealSection";
import ParallaxTripCard from "@/components/ParallaxTripCard";
import AnimatedCounters from "@/components/AnimatedCounters";
import BlogCarousel3D from "@/components/BlogCarousel3D";
import { WebsiteSchema, OrganizationSchema, SiteNavigationSchema, FAQSchema, WebPageSchema } from "@/components/StructuredData";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useHomepageTrips, useLikedTripIds, useRecentBlogPosts } from "@/hooks/useHomepageData";
import { useQueryClient } from "@tanstack/react-query";
import { formatBlogDate } from "@/hooks/useBlogPosts";

import tripMountains from "@/assets/trip-mountains.webp";

const Index = () => {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: trips = [], isLoading: loading } = useHomepageTrips();
  const { data: likedTripIds = new Set<string>() } = useLikedTripIds();
  
  const currentLang = i18n.language?.substring(0, 2) || "fr";
  const { data: recentPosts = [], isLoading: blogLoading } = useRecentBlogPosts(currentLang);

  const handleCreateClick = useCallback(() => {
    if (!user) {
      setAuthDialogOpen(true);
    } else {
      setCreateDialogOpen(true);
    }
  }, [user]);

  const handleTripCreated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["homepage-trips"] });
  }, [queryClient]);

  const handleTripClick = useCallback((trip: any) => {
    navigate(getLocalizedPath(`/trip/${trip.slug || trip.id}`));
  }, [navigate, getLocalizedPath]);

  const handleLikeToggle = useCallback(async (tripId: string) => {
    if (!user) {
      setAuthDialogOpen(true);
      return;
    }

    const isLiked = likedTripIds.has(tripId);

    if (isLiked) {
      const { error } = await supabase
        .from("followed_trips")
        .delete()
        .eq("user_id", user.id)
        .eq("trip_id", tripId);

      if (!error) {
        queryClient.invalidateQueries({ queryKey: ["liked-trip-ids"] });
        toast({ title: t('common.success'), description: t('common.success') });
      } else {
        toast({ title: t('common.error'), description: t('common.error'), variant: "destructive" });
      }
    } else {
      const { error } = await supabase
        .from("followed_trips")
        .insert({ user_id: user.id, trip_id: tripId });

      if (!error) {
        queryClient.invalidateQueries({ queryKey: ["liked-trip-ids"] });
        toast({ title: t('common.success'), description: t('common.success') });
      } else {
        toast({ title: t('common.error'), description: t('common.error'), variant: "destructive" });
      }
    }
  }, [user, likedTripIds, toast, t, queryClient]);

  const homepageFaqItems = [
    { question: t('home.faq.q1'), answer: t('home.faq.a1') },
    { question: t('home.faq.q2'), answer: t('home.faq.a2') },
    { question: t('home.faq.q3'), answer: t('home.faq.a3') },
    { question: t('home.faq.q4'), answer: t('home.faq.a4') },
    { question: t('home.faq.q5'), answer: t('home.faq.a5') },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={t('hero.title')}
        description={t('hero.description')}
        url="/"
      />
      <WebsiteSchema />
      <OrganizationSchema />
      <SiteNavigationSchema />
      <FAQSchema items={homepageFaqItems} />
      <WebPageSchema
        url="https://explaora.com/fr"
        name={t('hero.title')}
        description={t('hero.description')}
        primaryImage="https://explaora.com/hero-travel.webp"
      />
      
      <Navbar onAuthClick={() => setAuthDialogOpen(true)} hideUntilScroll />
      <Hero onCreateClick={handleCreateClick} />
      
      {/* Smooth transition from dark hero to light content */}
      <div className="relative h-24 -mt-1">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(220,35%,4%)] via-[hsl(220,20%,8%)] to-background" />
      </div>

      <main className="container mx-auto px-4 py-8">
        {/* Community Trips */}
        <ScrollRevealSection variant="fade-up">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">{t('home.communityTrips')}</h2>
            <p className="text-muted-foreground text-sm">
              {t('home.communityDescription')}
            </p>
          </div>
        </ScrollRevealSection>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <TripCardSkeleton key={i} />
            ))}
          </div>
        ) : trips.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">{t('home.noTrips')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trips.map((trip, index) => (
              <ScrollRevealSection key={trip.id} variant="fade-up" delay={index * 80}>
                <ParallaxTripCard>
                  <TripCard
                    tripId={trip.id}
                    title={trip.title}
                    country={trip.countries.join(", ")}
                    startDate={trip.start_date}
                    endDate={trip.end_date}
                    image={trip.cover_image || ""}
                    tripType={trip.trip_type}
                    showLikeButton={user?.id !== trip.user_id}
                    isLiked={likedTripIds.has(trip.id)}
                    onLikeToggle={() => handleLikeToggle(trip.id)}
                    onClick={() => handleTripClick(trip)}
                    priority={index < 3}
                  />
                </ParallaxTripCard>
              </ScrollRevealSection>
            ))}
          </div>
        )}
        
        <ScrollRevealSection variant="fade-up" delay={100}>
          <div className="text-center mt-6 mb-12">
            <div className="inline-block hover:scale-105 active:scale-95 transition-transform">
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate(getLocalizedPath("/explore"))}
                className="group"
              >
                {t('home.viewAllTrips')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </ScrollRevealSection>
      </main>

      {/* Animated Counters — full width */}
      <AnimatedCounters />

      <main className="container mx-auto px-4 py-12">
        {/* Popular Countries */}
        <ScrollRevealSection variant="fade-up">
          <PopularCountries />
        </ScrollRevealSection>

        {/* Recent Blog Articles — 3D Carousel */}
        {(blogLoading || recentPosts.length > 0) && (
          <section className="mt-12">
            <ScrollRevealSection variant="fade-up">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-1">{t('home.recentArticles')}</h2>
                <p className="text-muted-foreground text-sm">
                  {t('home.recentArticlesDescription')}
                </p>
              </div>
            </ScrollRevealSection>

            {blogLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <BlogCardSkeleton />
                <BlogCardSkeleton />
                <BlogCardSkeleton />
              </div>
            ) : (
              <ScrollRevealSection variant="fade-up" delay={100}>
                <BlogCarousel3D posts={recentPosts} />
              </ScrollRevealSection>
            )}

            <ScrollRevealSection variant="fade-up" delay={200}>
              <div className="text-center mt-6">
                <div className="inline-block hover:scale-105 active:scale-95 transition-transform">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(getLocalizedPath("/blog"))}
                    className="group"
                  >
                    {t('home.viewAllArticles')}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            </ScrollRevealSection>
          </section>
        )}
      </main>

      {/* Agences de voyage CTA */}
      <section className="bg-gradient-to-br from-primary/5 via-primary/10 to-secondary/5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-1 text-center md:text-left space-y-4">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full">
                {t('home.agencyBadge', 'Pour les professionnels')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold">
                {t('home.agencyTitle', 'Vous êtes une agence de voyage ?')}
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-lg">
                {t('home.agencyDescription', 'Découvrez Explaora en marque blanche : gérez vos clients, proposez des itinéraires personnalisés et développez votre activité avec notre plateforme clé en main.')}
              </p>
              <div className="pt-2">
                <Button
                  size="lg"
                  onClick={() => navigate(getLocalizedPath("/agencies"))}
                  className="group shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  {t('home.agencyCta', 'Découvrir l\'offre agences')}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="relative w-48 h-48 md:w-64 md:h-64 rounded-3xl overflow-hidden shadow-lg group">
                <img src={agencyImage} alt="Travel agency team" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" width={640} height={640} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
      <CreateTripDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen}
        onTripCreated={handleTripCreated}
      />
    </div>
  );
};

export default Index;
